import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import TaskCard from './TaskCard';
import axios from 'axios';
import { useAuth } from '../../Contexts/AuthContext/AuthContext';
import './vStyles/ProjectTasks.css';

interface Project {
  id: number;
  name: string;
  description: string;
  status: string;
}

interface Task {
  id: number;
  title: string;
  description: string;
  project_id: number;
  project_name?: string;
  required_skills: string[];
  location: string;
  due_date?: string;
  applied?: boolean;
  capacity?: number;
  participant_count?: number;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://mellow-commitment-production-bf3d.up.railway.app';

const ProjectTasks: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { volunteerId } = useAuth();
  
  const [project, setProject] = useState<Project | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [appliedTaskIds, setAppliedTaskIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (projectId) {
      fetchProjectDetails();
      fetchProjectTasks();
      fetchAppliedTasks();
    }
  }, [projectId]);

  const fetchProjectDetails = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/projects/${projectId}`);
      setProject(response.data as Project);
    } catch (err) {
      console.error('Error fetching project details:', err);
      setError('Failed to load project details');
    }
  };

  const fetchProjectTasks = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/projects/${projectId}/tasks`);
      setTasks(response.data as Task[]);
    } catch (err) {
      console.error('Error fetching project tasks:', err);
      setError('Failed to load project tasks');
    } finally {
      setLoading(false);
    }
  };

  const fetchAppliedTasks = async () => {
    if (!volunteerId) return;
    
    try {
      const response = await axios.get(`${API_BASE_URL}/api/volunteer/${volunteerId}/applied-tasks`, {
        headers: {
          'user-id': volunteerId.toString(),
          'Content-Type': 'application/json'
        }
      });
      setAppliedTaskIds(response.data as number[]);
    } catch (err) {
      console.error('Error fetching applied tasks:', err);
    }
  };

  const handleTaskApply = async (taskId: number) => {
    // Refresh applied tasks list
    await fetchAppliedTasks();
  };

  if (loading) {
    return (
      <div className="project-tasks-container">
        <div className="loading">Loading project tasks...</div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="project-tasks-container">
        <div className="error">{error || 'Project not found'}</div>
        <Link to="/volunteer/projects" className="back-link">
          <ArrowLeft size={16} /> Back to Projects
        </Link>
      </div>
    );
  }

  return (
    <div className="project-tasks-container">
      <div className="project-header">
        <button onClick={() => navigate(-1)} className="back-button">
          <ArrowLeft size={16} /> Back
        </button>
        <h1>{project.name}</h1>
        <p className="project-description">{project.description}</p>
      </div>

      <div className="tasks-section">
        <h2>Available Tasks</h2>
        {tasks.length === 0 ? (
          <div className="no-tasks">
            <p>No tasks available for this project yet.</p>
          </div>
        ) : (
          <div className="tasks-grid">
            {tasks.map(task => (
              <TaskCard
                key={task.id}
                task={task}
                onApply={handleTaskApply}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectTasks;
